"use client";

import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import ImagePlaceholder from "@/components/shared/ImagePlaceholder";
import FilterTabs from "@/components/public/FilterTabs";
import { usePageSize, PageSizeSelect } from "@/lib/usePageSize";
import { content } from "@/config/content";
import { useSiteSettings } from "@/lib/useSiteSettings";
import { sortCategories } from "@/lib/sortCategories";
import { useSearchParams, useRouter } from "next/navigation";
import ShareButton from "@/components/shared/ShareButton";
import { buildSharePayload } from "@/lib/shareUrl";
import { canPreselect } from "@/lib/stickersPreselectHelpers";
import StorePickerModal from "@/components/shared/StorePickerModal";
import RegionSelector, {
  CheckoutNotice,
} from "@/components/public/RegionSelector";
import FpsPaymentPanel from "@/components/public/FpsPaymentPanel";
import LineAddFriendButton from "@/components/public/LineAddFriendButton";
import InstagramDmButton from "@/components/public/InstagramDmButton";
import { useBottomBarOffset, useCheckoutOpen } from "@/lib/checkoutChrome";
import {
  DEFAULT_COUNTRY_CODE,
  REGION,
  buildOverseasConfig,
  computeShippingFee,
  isRegionCheckoutBlocked,
  twdToHkd,
  validateOverseasOrder,
} from "@/lib/overseasCheckout";

// 2026-10 海外寄送:地址欄的提示文字
const ADDRESS_PLACEHOLDER = {
  HK: "例:九龍旺角彌敦道 123 號 4 樓 A 室",
  CN: "例:廣東省深圳市南山區科技園路 1 號 2 棟 301",
};

// 電話欄提示:寫成「一看就是說明」的樣子,不像已經填好的號碼(手機走查 #6)
const PHONE_PLACEHOLDER = {
  HK: "8 位數字,例:9123 4567",
  CN: "11 位數字,例:138 1234 5678",
};

export default function StickersClient({ artworks, copy = {} }) {
  const { settings } = useSiteSettings("pricing");
  const pack5Label = copy.pack5Label || "5入組";
  const pack10Label = copy.pack10Label || "10入組";
  const checkoutButton = copy.checkoutButton || "結帳寄送貼紙";
  const pickStoreButton = copy.pickStoreButton || "選擇門市";
  const continueShoppingButton = copy.continueShoppingButton || "繼續選購";
  const soldOutLabel = copy.soldOutLabel || "SOLD OUT";
  const shippingFormHeading = copy.shippingFormHeading || "寄送資訊";
  const selectedCountTemplate =
    copy.selectedCountTemplate || "已選 {count} 張貼紙";
  const successHeading = copy.successHeading || "訂單已送出";
  const successMessage =
    copy.successMessage || "我們會盡快確認您的訂單，感謝您的支持！";
  const PACK_OPTIONS = useMemo(
    () => [
      {
        count: 5,
        price: Number(settings.sticker_pack_5_price || 450),
        label: pack5Label,
      },
      {
        count: 10,
        price: Number(settings.sticker_pack_10_price || 750),
        label: pack10Label,
      },
    ],
    [
      settings.sticker_pack_5_price,
      settings.sticker_pack_10_price,
      pack5Label,
      pack10Label,
    ],
  );
  const SHIPPING_FEE = Number(settings.sticker_shipping_fee || 60);
  // 2026-10 海外購買(香港 FPS / 中國微信支付);設定在後台「海外購買」
  const { settings: overseasSettings } = useSiteSettings("overseas");
  // brand 設定帶進來是為了 brand_line_id(「加官方 LINE」按鈕的預設連結)
  const { settings: brandSettings } = useSiteSettings("brand");
  const overseas = useMemo(
    () => buildOverseasConfig({ ...brandSettings, ...overseasSettings }),
    [brandSettings, overseasSettings],
  );
  const [region, setRegion] = useState(REGION.TW);
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    DEFAULT_COUNTRY_CODE.HK,
  );
  const [fpsResult, setFpsResult] = useState(null);
  const isOverseas = region !== REGION.TW;
  const regionBlocked = isRegionCheckoutBlocked(region, overseas);
  const shippingFee = computeShippingFee(region, {
    twShipping: SHIPPING_FEE,
    config: overseas,
  });
  const handleRegionChange = (next) => {
    setRegion(next);
    if (DEFAULT_COUNTRY_CODE[next])
      setPhoneCountryCode(DEFAULT_COUNTRY_CODE[next]);
  };
  const [selected, setSelected] = useState([]);
  const [pack, setPack] = useState(5);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [activeFilter, setActiveFilter] = useState(content.gallery.filterAll);
  const [currentPage, setCurrentPage] = useState(1);
  const { pageSize, setPageSize, PAGE_OPTIONS } = usePageSize();
  const [form, setForm] = useState({
    buyerName: "",
    buyerPhone: "",
    buyerEmail: "",
    shippingStore: "",
    shippingAddress: "",
    note: "",
  });
  const [storePickerOpen, setStorePickerOpen] = useState(false);

  const gridRef = useRef(null);
  // 底部「已選 N 張 / 結帳」條:讓 Cookie 提示框推到它上面;表單開著時藏 IG 浮動鈕
  const stickyBarRef = useRef(null);
  useBottomBarOffset(stickyBarRef, selected.length > 0 && !showForm);
  useCheckoutOpen(showForm);

  const openStorePicker = useCallback(() => setStorePickerOpen(true), []);
  const handleStorePicked = useCallback(({ storeName, address }) => {
    setForm((prev) => ({
      ...prev,
      shippingStore: storeName || prev.shippingStore,
      shippingAddress: address || prev.shippingAddress,
    }));
  }, []);

  const maxSelections = pack;
  const currentPack = PACK_OPTIONS.find((p) => p.count === pack);

  // === 過濾 & 分類 ===
  const displayArtworks = useMemo(
    () =>
      artworks
        .filter((a) => a.visible !== false && !a.deletedAt)
        .map((a) => ({ ...a, image: a.imageV2 || a.image })),
    [artworks],
  );

  const searchParams = useSearchParams();
  const router = useRouter();

  useEffect(() => {
    const preselectId = searchParams.get("preselect");
    const check = canPreselect({
      preselectId,
      artworks: displayArtworks,
      alreadySelected: selected,
    });
    if (!check.ok) {
      if (preselectId) {
        router.replace("/stickers", { scroll: false });
      }
      return;
    }
    setSelected((prev) =>
      prev.includes(check.target.id) ? prev : [...prev, check.target.id],
    );
    router.replace("/stickers", { scroll: false });
    requestAnimationFrame(() => {
      gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayArtworks, searchParams]);

  const dynamicFilters = useMemo(
    () =>
      sortCategories([
        ...new Set(displayArtworks.map((a) => a.category).filter(Boolean)),
      ]),
    [displayArtworks],
  );

  const filtered =
    activeFilter === content.gallery.filterAll
      ? displayArtworks
      : displayArtworks.filter((a) => a.category === activeFilter);

  // === 分頁 ===
  const totalPages = Math.ceil(filtered.length / pageSize);
  const startIdx = (currentPage - 1) * pageSize;
  const visible = filtered.slice(startIdx, startIdx + pageSize);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getPageNumbers = () => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, "...", totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages,
      );
    }
    return pages;
  };

  // === 選取邏輯 ===
  const isSoldOut = (artwork) => artwork.stock === 0;

  const handlePackChange = (newCount) => {
    setPack(newCount);
    setSelected((prev) =>
      prev.length > newCount ? prev.slice(0, newCount) : prev,
    );
  };

  const toggleSelect = (artworkId) => {
    setSelected((prev) => {
      if (prev.includes(artworkId)) {
        return prev.filter((id) => id !== artworkId);
      }
      if (prev.length >= maxSelections) return prev;
      return [...prev, artworkId];
    });
  };

  // 取得已選圖的資料（用於 sticky bar 縮圖）
  const selectedArtworks = useMemo(
    () =>
      selected
        .map((id) => displayArtworks.find((a) => a.id === id))
        .filter(Boolean),
    [selected, displayArtworks],
  );

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOverseasSubmit = async () => {
    const checked = validateOverseasOrder({
      region,
      product: "sticker",
      buyerName: form.buyerName,
      buyerEmail: form.buyerEmail,
      phoneCountryCode,
      buyerPhone: form.buyerPhone,
      shippingAddress: form.shippingAddress,
    });
    if (!checked.ok) {
      alert(checked.errors[0]);
      return;
    }
    if (regionBlocked) {
      alert(overseas.wechatPendingNotice);
      return;
    }
    setSubmitting(true);
    try {
      const items = selected.map((id) => {
        const art = artworks.find((a) => a.id === id);
        return { id: art.id, name: art.name, image: art.image };
      });
      const res = await fetch("/api/sticker-orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": crypto.randomUUID(),
        },
        body: JSON.stringify({
          region,
          items,
          pack,
          buyerName: form.buyerName.trim(),
          phoneCountryCode,
          buyerPhone: form.buyerPhone.trim(),
          buyerEmail: form.buyerEmail.trim(),
          shippingAddress: form.shippingAddress.trim(),
          note: form.note.trim() || null,
        }),
      });
      const data = await res.json();
      if (data.success && data.paymentHtml) {
        // 中國:綠界微信支付
        const div = document.createElement("div");
        div.style.display = "none";
        div.innerHTML = data.paymentHtml;
        document.body.appendChild(div);
        const ecpayForm = div.querySelector("form");
        if (ecpayForm) ecpayForm.submit();
        return;
      }
      if (data.success && data.fps) {
        // 香港:顯示轉數快付款資訊(同一份也寄到 Email)
        setFpsResult({ ...data.fps, statusUrl: data.statusUrl || null });
        setSelected([]);
        setForm({
          buyerName: "",
          buyerPhone: "",
          buyerEmail: "",
          shippingStore: "",
          shippingAddress: "",
          note: "",
        });
        setShowForm(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (data.success) {
        setSuccess(data.orderNo);
        setShowForm(false);
        return;
      }
      alert(data.error || "送出失敗，請稍後再試");
    } catch {
      alert("送出失敗，請稍後再試");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) return;
    if (isOverseas) {
      await handleOverseasSubmit();
      return;
    }
    if (!form.buyerName.trim() || !form.buyerPhone.trim()) return;
    if (!form.buyerEmail.trim() || !form.buyerEmail.includes("@")) {
      alert("請填寫正確的 Email");
      return;
    }
    if (!form.shippingStore.trim()) {
      alert("請填寫 7-11 取貨店名");
      return;
    }

    setSubmitting(true);
    try {
      const items = selected.map((id) => {
        const art = artworks.find((a) => a.id === id);
        return { id: art.id, name: art.name, image: art.image };
      });

      // Idempotency-Key lets the backend dedupe this exact submission
      // (network retry, React double-fire, etc). Paired with the partial
      // UNIQUE index on sticker_orders.idempotency_key added in the
      // 2026-04-24 migration.
      const idempotencyKey = crypto.randomUUID();
      const res = await fetch("/api/sticker-orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify({
          items,
          pack,
          packPrice: currentPack.price,
          shippingFee: SHIPPING_FEE,
          total: currentPack.price + SHIPPING_FEE,
          buyerName: form.buyerName.trim(),
          buyerPhone: form.buyerPhone.trim(),
          buyerEmail: form.buyerEmail.trim(),
          shippingStore: form.shippingStore.trim(),
          shippingAddress: form.shippingAddress.trim() || null,
          note: form.note.trim() || null,
        }),
      });

      const data = await res.json();
      if (data.success && data.paymentHtml) {
        // 導向 ECPay 付款：插入自動提交表單
        const div = document.createElement("div");
        div.style.display = "none";
        div.innerHTML = data.paymentHtml;
        document.body.appendChild(div);
        const ecpayForm = div.querySelector("form");
        if (ecpayForm) ecpayForm.submit();
        return; // 頁面即將跳轉，不需要清理 state
      } else if (data.success) {
        // fallback：Supabase 未設定時無 paymentHtml
        setSuccess(data.orderNo);
        setSelected([]);
        setForm({
          buyerName: "",
          buyerPhone: "",
          buyerEmail: "",
          shippingStore: "",
          shippingAddress: "",
          note: "",
        });
        setShowForm(false);
      } else {
        alert(data.error || "送出失敗，請稍後再試");
      }
    } catch {
      alert("送出失敗，請稍後再試");
    } finally {
      setSubmitting(false);
    }
  };

  // === 香港 FPS:訂單已成立,等轉帳 ===
  if (fpsResult) {
    return (
      <section
        style={{
          padding: "var(--spacing-sectionGap) var(--spacing-pagePadding)",
        }}
      >
        <div className="max-w-md mx-auto">
          <div className="text-center mb-5">
            <div className="text-4xl mb-3">&#10003;</div>
            <h2
              className="text-xl font-bold mb-1"
              style={{
                fontFamily: "var(--font-heading)",
                color: "var(--color-text)",
              }}
            >
              訂單已成立
            </h2>
            <p className="text-sm" style={{ color: "var(--color-textMuted)" }}>
              付款資訊也已寄到你的 Email
            </p>
          </div>
          <FpsPaymentPanel
            fps={fpsResult}
            lineUrl={overseas.lineUrl}
            igUrl={overseas.igUrl}
          />
          <div className="flex flex-col gap-2 mt-5">
            {fpsResult.statusUrl && (
              <a
                href={fpsResult.statusUrl}
                className="w-full py-2.5 text-sm font-medium text-center"
                style={{
                  color: "var(--color-accent)",
                  border: "1px solid var(--color-accent)",
                  borderRadius: "var(--radius-full)",
                }}
              >
                查看訂單狀態
              </a>
            )}
            <button
              onClick={() => setFpsResult(null)}
              className="w-full py-2.5 text-sm font-medium transition-opacity hover:opacity-80"
              style={{
                backgroundColor: "var(--color-accent)",
                color: "#fff",
                borderRadius: "var(--radius-full)",
                border: "none",
                cursor: "pointer",
              }}
            >
              {continueShoppingButton}
            </button>
          </div>
        </div>
      </section>
    );
  }

  // === 成功畫面 ===
  if (success) {
    return (
      <section
        style={{
          padding: "var(--spacing-sectionGap) var(--spacing-pagePadding)",
        }}
      >
        <div
          className="max-w-md mx-auto text-center p-8"
          style={{
            backgroundColor: "var(--color-cardBg)",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--color-cardBorder)",
          }}
        >
          <div className="text-4xl mb-4">&#10003;</div>
          <h2
            className="text-xl font-bold mb-2"
            style={{
              fontFamily: "var(--font-heading)",
              color: "var(--color-text)",
            }}
          >
            {successHeading}
          </h2>
          <p
            className="text-sm mb-4"
            style={{ color: "var(--color-textMuted)" }}
          >
            訂單編號：
            <span style={{ color: "var(--color-accent)" }}>{success}</span>
          </p>
          <p
            className="text-sm mb-6"
            style={{ color: "var(--color-textMuted)" }}
          >
            {successMessage}
          </p>
          <button
            onClick={() => setSuccess(null)}
            className="px-6 py-2.5 text-sm font-medium transition-opacity hover:opacity-80"
            style={{
              backgroundColor: "var(--color-accent)",
              color: "#fff",
              borderRadius: "var(--radius-full)",
              border: "none",
              cursor: "pointer",
            }}
          >
            {continueShoppingButton}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      style={{
        padding: "0 var(--spacing-pagePadding) var(--spacing-sectionGap)",
      }}
    >
      <div className="max-w-5xl mx-auto">
        {/* 組合選擇 */}
        <div className="flex gap-3 mb-4 px-1">
          {PACK_OPTIONS.map((opt) => (
            // a11y: replace div[role=button] with real <button> element.
            // Avoids "div with button role that has tabindex but no valid button semantics".
            <button
              key={opt.count}
              type="button"
              aria-pressed={pack === opt.count}
              onClick={() => handlePackChange(opt.count)}
              style={{
                position: "relative",
                flex: 1,
                textAlign: "center",
                padding: "16px",
                backgroundColor: "var(--color-cardBg)",
                border:
                  pack === opt.count
                    ? "2px solid var(--color-accent)"
                    : "1px solid var(--color-cardBorder)",
                borderRadius: "var(--radius-md)",
                cursor: "pointer",
                boxShadow:
                  pack === opt.count ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
                WebkitTapHighlightColor: "transparent",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}
            >
              {/* 最划算 badge — 引導用戶選更大組合（conversion 最佳實踐） */}
              {opt.count === 10 && (
                <span
                  style={{
                    position: "absolute",
                    top: "-9px",
                    right: "12px",
                    backgroundColor: "var(--color-accent)",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    padding: "2px 8px",
                    borderRadius: "var(--radius-full)",
                    fontFamily: "var(--font-heading)",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                  }}
                >
                  最划算
                </span>
              )}
              <p
                className="text-sm font-bold mb-1"
                style={{
                  fontFamily: "var(--font-heading)",
                  color: "var(--color-text)",
                }}
              >
                {opt.label}
              </p>
              <p
                className="text-xs"
                style={{ color: "var(--color-accent)", fontWeight: 600 }}
              >
                NT${opt.price}
              </p>
            </button>
          ))}
        </div>

        {/* 分類篩選 + 每頁數量 */}
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <FilterTabs
            active={activeFilter}
            onChange={handleFilterChange}
            filters={dynamicFilters}
          />
          <PageSizeSelect
            pageSize={pageSize}
            onChange={(n) => {
              setPageSize(n);
              setCurrentPage(1);
            }}
            options={PAGE_OPTIONS}
          />
        </div>

        {/* Grid 錨點 + 當前分類計數 */}
        <div
          ref={gridRef}
          className="flex items-center justify-between mb-4 px-1"
        >
          <p className="text-sm" style={{ color: "var(--color-textMuted)" }}>
            {filtered.length} 張可選
          </p>
          {totalPages > 1 && (
            <p className="text-xs" style={{ color: "var(--color-textMuted)" }}>
              第 {currentPage} / {totalPages} 頁
            </p>
          )}
        </div>

        {/* 作品選擇 Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {visible.map((artwork, index) => {
            const soldOut = isSoldOut(artwork);
            const isSelected = selected.includes(artwork.id);

            return (
              // a11y fix (axe: nested-interactive — 50 cards):
              // outer changed from role="button" + tabIndex to a non-interactive <article>.
              // A dedicated invisible <button> overlay below handles card selection,
              // so the ShareButton inside is no longer nested inside an interactive element.
              <article
                key={artwork.id}
                data-artwork-id={artwork.id}
                className={`sticker-card relative overflow-hidden ${isSelected ? "is-selected" : ""}`}
                style={{
                  backgroundColor: "var(--color-cardBg)",
                  borderRadius: "var(--radius-md)",
                  border: isSelected
                    ? "2px solid var(--color-accent)"
                    : "1px solid var(--color-cardBorder)",
                  boxShadow: isSelected
                    ? "0 0 0 1px var(--color-accent), 0 8px 28px rgba(232,160,191,0.22)"
                    : "var(--shadow-card)",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {/* a11y: dedicated focusable control for card selection.
                    Covers the full card (z-0) but sits below ShareButton (z-10),
                    so keyboard focus + click select the sticker without nesting. */}
                <button
                  type="button"
                  onClick={() => !soldOut && toggleSelect(artwork.id)}
                  disabled={soldOut}
                  aria-pressed={isSelected}
                  aria-label={`${isSelected ? "取消選擇" : "選擇"} ${artwork.name}${soldOut ? "（已售完）" : ""}`}
                  className="absolute inset-0 w-full h-full z-0"
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: soldOut ? "not-allowed" : "pointer",
                    padding: 0,
                  }}
                />
                <div className="relative group pointer-events-none">
                  <div
                    style={{
                      filter: soldOut
                        ? "grayscale(100%) brightness(0.78)"
                        : "none",
                      transition: "filter 0.3s ease",
                    }}
                  >
                    <ImagePlaceholder
                      name={artwork.name}
                      image={artwork.image}
                      artworkId={artwork.id}
                      aspectRatio="4/5"
                      className="w-full"
                    />
                  </div>

                  {/* 分享 pill — 左上，文字避免「🎁 = 禮物」誤讀；首頁第一張 pulse 吸引目光
                      a11y: re-enable pointer events here (parent has pointer-events-none)
                      so the ShareButton stays clickable above the selection overlay.
                      a11y fix (axe: color-contrast): don't fade the share button on
                      sold-out cards — the 0.4 opacity dropped accent bg to 2.57:1. */}
                  <div
                    className="absolute top-2 left-2 z-10"
                    style={{
                      pointerEvents: "auto",
                    }}
                  >
                    <ShareButton
                      payload={buildSharePayload({ artwork, kind: "sticker" })}
                      variant="pill"
                      size="sm"
                      placement="card"
                      emoji="📤"
                      label="分享"
                      className={
                        index === 0 && currentPage === 1
                          ? "animate-share-pulse"
                          : ""
                      }
                    />
                  </div>

                  {/* Sold out overlay */}
                  {soldOut && (
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{
                        backgroundColor: "rgba(0,0,0,0.55)",
                        backdropFilter: "blur(2px)",
                        WebkitBackdropFilter: "blur(2px)",
                      }}
                    >
                      <span
                        className="text-sm font-bold uppercase px-5 py-2"
                        style={{
                          color: "#fff",
                          border: "2px solid rgba(255,255,255,0.9)",
                          borderRadius: "var(--radius-full)",
                          letterSpacing: "0.18em",
                          fontFamily: "var(--font-heading)",
                        }}
                      >
                        {soldOutLabel}
                      </span>
                    </div>
                  )}

                  {/* Selected check */}
                  {isSelected && (
                    <div
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center"
                      style={{
                        backgroundColor: "var(--color-accent)",
                        borderRadius: "50%",
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  )}
                </div>

                <div className="p-2 md:p-2.5 pointer-events-none">
                  <p
                    className="text-xs md:text-sm font-medium truncate"
                    style={{
                      fontFamily: "var(--font-heading)",
                      color: "var(--color-text)",
                      opacity: soldOut ? 0.55 : 1,
                      textDecoration: soldOut ? "line-through" : "none",
                    }}
                  >
                    {artwork.name}
                  </p>
                </div>
              </article>
            );
          })}
        </div>

        {/* 空狀態 */}
        {filtered.length === 0 && (
          <p
            className="text-center py-12"
            style={{
              color: "var(--color-textMuted)",
              fontFamily: "var(--font-body)",
            }}
          >
            這個分類還沒有作品
          </p>
        )}

        {/* 分頁 */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center gap-3 mt-10">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-11 px-3 flex items-center justify-center text-sm font-medium gap-1 transition-all duration-200"
                style={{
                  borderRadius: "var(--radius-full)",
                  border: "1px solid var(--color-cardBorder)",
                  backgroundColor: "transparent",
                  color: "var(--color-text)",
                  opacity: currentPage === 1 ? 0.35 : 1,
                  cursor: currentPage === 1 ? "default" : "pointer",
                }}
              >
                ← 上一頁
              </button>

              {getPageNumbers().map((page, i) =>
                page === "..." ? (
                  <span
                    key={`dots-${i}`}
                    className="w-11 h-11 flex items-center justify-center text-sm"
                    style={{ color: "var(--color-textMuted)" }}
                  >
                    ⋯
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className="w-11 h-11 flex items-center justify-center text-sm font-medium transition-all duration-200"
                    style={{
                      borderRadius: "var(--radius-full)",
                      border:
                        page === currentPage ? "none" : "1px solid transparent",
                      backgroundColor:
                        page === currentPage
                          ? "var(--color-accent)"
                          : "transparent",
                      color:
                        page === currentPage
                          ? "var(--color-accentText)"
                          : "var(--color-textMuted)",
                      cursor: "pointer",
                    }}
                  >
                    {page}
                  </button>
                ),
              )}

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="h-11 px-3 flex items-center justify-center text-sm font-medium gap-1 transition-all duration-200"
                style={{
                  borderRadius: "var(--radius-full)",
                  border: "1px solid var(--color-cardBorder)",
                  backgroundColor: "transparent",
                  color: "var(--color-text)",
                  opacity: currentPage === totalPages ? 0.35 : 1,
                  cursor: currentPage === totalPages ? "default" : "pointer",
                }}
              >
                下一頁 →
              </button>
            </div>

            <p className="text-xs" style={{ color: "var(--color-textMuted)" }}>
              第 {currentPage} 頁 / 共 {totalPages} 頁（{filtered.length}{" "}
              張作品）
            </p>
          </div>
        )}

        {/* Sticky 選取 Bar — 選了圖才顯示，表單開啟時隱藏 */}
        {selected.length > 0 && !showForm && (
          <div
            ref={stickyBarRef}
            className="fixed bottom-0 left-0 right-0 z-30"
            style={{
              backgroundColor: "var(--color-cardBg)",
              borderTop: "1px solid var(--color-cardBorder)",
              boxShadow: "0 -4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <div
              className="max-w-5xl mx-auto flex items-center justify-between gap-3 px-4 py-3"
              style={{ paddingBottom: "calc(12px + var(--safe-area-bottom))" }}
            >
              {/* 左側：已選縮圖 */}
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <div className="flex -space-x-2 shrink-0">
                  {selectedArtworks.slice(0, 5).map((art) => (
                    <div
                      key={art.id}
                      className="w-9 h-9 rounded-full overflow-hidden border-2 shrink-0"
                      style={{ borderColor: "var(--color-cardBg)" }}
                    >
                      <ImagePlaceholder
                        name={art.name}
                        image={art.image}
                        artworkId={art.id}
                        aspectRatio="1/1"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  {selectedArtworks.length > 5 && (
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0"
                      style={{
                        borderColor: "var(--color-cardBg)",
                        backgroundColor: "var(--color-bgSecondary)",
                        color: "var(--color-textMuted)",
                      }}
                    >
                      +{selectedArtworks.length - 5}
                    </div>
                  )}
                </div>
                <span
                  className="text-sm font-medium ml-2 truncate"
                  style={{ color: "var(--color-text)" }}
                >
                  {selected.length}/{maxSelections} 張
                </span>
              </div>

              {/* 右側：結帳按鈕 — 未選滿時明確顯示還差幾張 */}
              {(() => {
                const remaining = maxSelections - selected.length;
                const ready = remaining === 0;
                return (
                  <button
                    onClick={() => ready && setShowForm(true)}
                    disabled={!ready}
                    className="shrink-0 px-5 py-2.5 text-sm font-medium transition-opacity hover:opacity-80 disabled:hover:opacity-100"
                    style={{
                      backgroundColor: ready
                        ? "var(--color-accent)"
                        : "var(--color-cardBorder)",
                      color: "#fff",
                      borderRadius: "var(--radius-full)",
                      border: "none",
                      cursor: ready ? "pointer" : "not-allowed",
                      opacity: ready ? 1 : 0.75,
                    }}
                  >
                    {ready ? checkoutButton : `再選 ${remaining} 張`}
                  </button>
                );
              })()}
            </div>
          </div>
        )}

        {/* 底部留白，避免 sticky bar 遮住內容 */}
        {selected.length > 0 && !showForm && <div style={{ height: "80px" }} />}

        {/* 訂購表單 Modal */}
        {showForm && (
          <div
            className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
            style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowForm(false);
            }}
          >
            <div
              className="w-full md:max-w-lg md:mx-4 max-h-[90vh] overflow-y-auto"
              style={{
                backgroundColor: "var(--color-cardBg)",
                borderRadius: "var(--radius-md) var(--radius-md) 0 0",
                border: "1px solid var(--color-cardBorder)",
              }}
              ref={(el) => {
                if (el && window.innerWidth >= 768) {
                  el.style.borderRadius = "var(--radius-md)";
                }
              }}
            >
              <div className="p-5 md:p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3
                    className="text-lg font-bold"
                    style={{
                      fontFamily: "var(--font-heading)",
                      color: "var(--color-text)",
                    }}
                  >
                    {shippingFormHeading}
                  </h3>
                  <button
                    onClick={() => setShowForm(false)}
                    className="w-11 h-11 flex items-center justify-center"
                    style={{
                      color: "var(--color-textMuted)",
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                    }}
                    aria-label="關閉"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>

                {/* 已選圖片預覽 */}
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                  {selectedArtworks.map((art) => (
                    <div
                      key={art.id}
                      className="w-12 h-12 rounded-lg overflow-hidden shrink-0 border"
                      style={{ borderColor: "var(--color-cardBorder)" }}
                    >
                      <ImagePlaceholder
                        name={art.name}
                        image={art.image}
                        artworkId={art.id}
                        aspectRatio="1/1"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>

                <p
                  className="text-sm mb-4"
                  style={{ color: "var(--color-textMuted)" }}
                >
                  {selectedCountTemplate.replace(
                    "{count}",
                    String(selected.length),
                  )}
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Field label="寄送地區" required>
                    <RegionSelector
                      value={region}
                      onChange={handleRegionChange}
                    />
                    {/* 選中國當下就提示(不等填完整張表才看到):未開通附 LINE / IG 按鈕+實名說明 */}
                    {region === REGION.CN && (
                      <div className="flex flex-col gap-2" style={{ marginTop: "10px" }}>
                        {regionBlocked && (
                          <CheckoutNotice
                            tone="warn"
                            text={overseas.wechatPendingNotice}
                          >
                            <LineAddFriendButton href={overseas.lineUrl} />
                            <InstagramDmButton href={overseas.igUrl} />
                          </CheckoutNotice>
                        )}
                        <CheckoutNotice
                          tone="note"
                          text={overseas.wechatRealnameNotice}
                        />
                      </div>
                    )}
                  </Field>

                  <Field label="姓名" required>
                    <input
                      type="text"
                      name="buyerName"
                      value={form.buyerName}
                      onChange={handleFormChange}
                      required
                      placeholder="請輸入姓名"
                      className="form-input"
                    />
                  </Field>

                  {isOverseas ? (
                    <Field label="電話(含國碼)" required>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          inputMode="tel"
                          name="phoneCountryCode"
                          aria-label="國碼"
                          value={phoneCountryCode}
                          onChange={(e) => setPhoneCountryCode(e.target.value)}
                          required
                          className="form-input"
                          style={{ width: "88px", flex: "none" }}
                        />
                        <input
                          type="tel"
                          name="buyerPhone"
                          aria-label="電話號碼"
                          value={form.buyerPhone}
                          onChange={handleFormChange}
                          required
                          placeholder={PHONE_PLACEHOLDER[region]}
                          className="form-input"
                          style={{ flex: 1, minWidth: 0 }}
                        />
                      </div>
                    </Field>
                  ) : (
                    <Field label="手機" required>
                      <input
                        type="tel"
                        name="buyerPhone"
                        value={form.buyerPhone}
                        onChange={handleFormChange}
                        required
                        placeholder="09xxxxxxxx"
                        className="form-input"
                      />
                    </Field>
                  )}

                  <Field label="Email" required>
                    <input
                      type="email"
                      name="buyerEmail"
                      value={form.buyerEmail}
                      onChange={handleFormChange}
                      required
                      placeholder="請輸入 Email（必填）"
                      className="form-input"
                    />
                  </Field>

                  {isOverseas ? (
                    <Field label="收件地址" required>
                      <textarea
                        name="shippingAddress"
                        value={form.shippingAddress}
                        onChange={handleFormChange}
                        required
                        rows={3}
                        placeholder={ADDRESS_PLACEHOLDER[region]}
                        className="form-input"
                        style={{ resize: "vertical" }}
                      />
                    </Field>
                  ) : (
                    <>
                      <Field label="7-11 取貨門市" required>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            name="shippingStore"
                            value={form.shippingStore}
                            onChange={handleFormChange}
                            required
                            placeholder="點右側按鈕選擇門市"
                            className="form-input"
                            style={{ flex: 1 }}
                          />
                          <button
                            type="button"
                            onClick={openStorePicker}
                            className="shrink-0 flex items-center gap-1.5 px-4 text-sm md:text-xs font-medium transition-opacity hover:opacity-80"
                            style={{
                              minHeight: "44px",
                              backgroundColor: "var(--color-accent)",
                              color: "#fff",
                              borderRadius: "8px",
                              border: "none",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                              <circle cx="12" cy="10" r="3" />
                            </svg>
                            {pickStoreButton}
                          </button>
                        </div>
                        {form.shippingStore && (
                          <p
                            className="mt-1.5 text-xs"
                            style={{ color: "var(--color-accent)" }}
                          >
                            已選：{form.shippingStore}
                          </p>
                        )}
                      </Field>

                      <Field label="取貨地址">
                        <input
                          type="text"
                          name="shippingAddress"
                          value={form.shippingAddress}
                          onChange={handleFormChange}
                          placeholder="選擇門市後自動填入"
                          className="form-input"
                          readOnly={
                            !!form.shippingAddress &&
                            !form.shippingAddress.includes("手動")
                          }
                        />
                      </Field>
                    </>
                  )}

                  <Field label="備註">
                    <textarea
                      name="note"
                      value={form.note}
                      onChange={handleFormChange}
                      placeholder="有什麼想告訴我們的嗎？"
                      rows={3}
                      className="form-input"
                      style={{ resize: "vertical" }}
                    />
                  </Field>

                  {/* 價格明細 */}
                  <div
                    style={{
                      padding: "12px 0 4px",
                      fontSize: "13px",
                      color: "var(--color-textMuted)",
                    }}
                  >
                    <div className="flex justify-between mb-1">
                      <span>貼紙 {currentPack.label}</span>
                      <span>NT${currentPack.price}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span>
                        運費
                        {isOverseas
                          ? `(寄${region === REGION.HK ? "香港" : "中國"})`
                          : ""}
                      </span>
                      <span>NT${shippingFee}</span>
                    </div>
                    <div
                      className="flex justify-between pt-2 font-semibold"
                      style={{
                        borderTop: "1px solid var(--color-cardBorder)",
                        color: "var(--color-text)",
                        fontSize: "14px",
                      }}
                    >
                      <span>合計</span>
                      <span>NT${currentPack.price + shippingFee}</span>
                    </div>
                    {region === REGION.HK && (
                      <div
                        className="flex justify-between mt-1"
                        style={{ color: "var(--color-accent)" }}
                      >
                        <span>轉數快應付(港幣)</span>
                        <span>
                          約 HK$
                          {twdToHkd(
                            currentPack.price + shippingFee,
                            overseas.hkdRate,
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  <CheckoutNotice text={overseas.leadTimeNotice} />

                  <button
                    type="submit"
                    disabled={
                      submitting ||
                      regionBlocked ||
                      selected.length !== currentPack?.count
                    }
                    className="w-full py-3 text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
                    style={{
                      backgroundColor: "var(--color-accent)",
                      color: "#fff",
                      borderRadius: "var(--radius-full)",
                      border: "none",
                      cursor: submitting ? "wait" : "pointer",
                      marginTop: "8px",
                    }}
                  >
                    {submitting
                      ? "處理中..."
                      : region === REGION.HK
                        ? "送出訂單(轉數快 FPS 付款)"
                        : region === REGION.CN
                          ? regionBlocked
                            ? "微信支付開通中"
                            : `前往微信支付 NT$${currentPack.price + shippingFee}`
                          : `前往付款 NT$${currentPack.price + SHIPPING_FEE}`}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form input + sticker card styles */}
      <style jsx>{`
        .form-input {
          width: 100%;
          padding: 12px 14px;
          font-size: 16px;
          background: var(--color-bg);
          color: var(--color-text);
          border: 1px solid var(--color-cardBorder);
          border-radius: 8px;
          outline: none;
          transition: border-color 0.2s;
        }
        .form-input:focus {
          border-color: var(--color-accent);
        }
        .form-input::placeholder {
          color: var(--color-textMuted);
        }

        /* 卡片 hover — 行業慣例：作品飄起來感（僅 hover 裝置） */
        .sticker-card {
          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease,
            border-color 0.25s ease;
        }
        @media (hover: hover) {
          .sticker-card:not(.is-selected):hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45);
            border-color: rgba(232, 160, 191, 0.35);
          }
        }
      `}</style>

      <StorePickerModal
        isOpen={storePickerOpen}
        onClose={() => setStorePickerOpen(false)}
        onSelect={handleStorePicked}
      />
    </section>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label
      className="block text-base md:text-sm font-medium mb-1.5"
      style={{ color: "var(--color-textSecondary)" }}
    >
      {label}
      {required && (
        <span style={{ color: "var(--color-accent)", marginLeft: "2px" }}>
          *
        </span>
      )}
    </label>
    {children}
  </div>
);
